import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Check, ChevronDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import type { Project, ProjectStatus } from '@/types/project';
import { useUpdateProjectStatus } from '@/hooks/projects/useUpdateProjectStatus';

interface Props {
  project: Project;
  canEdit?: boolean;
}

const STATUS_LABELS: Record<string, string> = {
  intake: 'Intake',
  tender: 'Tender',
  build: 'Build',
  defects: 'Defects',
  archive: 'Archive',
  service: 'Service',
  inactive: 'Inactive',
};

const STATUS_COLORS: Record<string, string> = {
  intake: 'bg-blue-50 text-blue-700 border-blue-200 dark:bg-blue-950/30 dark:text-blue-300 dark:border-blue-800',
  tender: 'bg-amber-50 text-amber-700 border-amber-200 dark:bg-amber-950/30 dark:text-amber-300 dark:border-amber-800',
  build: 'bg-emerald-50 text-emerald-700 border-emerald-200 dark:bg-emerald-950/30 dark:text-emerald-300 dark:border-emerald-800',
  defects: 'bg-orange-50 text-orange-700 border-orange-200 dark:bg-orange-950/30 dark:text-orange-300 dark:border-orange-800',
  archive: 'bg-slate-50 text-slate-600 border-slate-200 dark:bg-slate-900/30 dark:text-slate-400 dark:border-slate-800',
  service: 'bg-purple-50 text-purple-700 border-purple-200 dark:bg-purple-950/30 dark:text-purple-300 dark:border-purple-800',
  inactive: 'bg-gray-50 text-gray-600 border-gray-200 dark:bg-gray-900/30 dark:text-gray-400 dark:border-gray-800',
};

const OPTIONS: Array<{ value: ProjectStatus; label: string }> = [
  { value: 'intake', label: 'Tiếp nhận' },
  { value: 'tender', label: 'Đấu thầu' },
  { value: 'build', label: 'Thi công' },
];

const isRlsError = (err: any): boolean => {
  const msg = (err?.message || '').toLowerCase();
  return (
    err?.code === '42501' ||
    msg.includes('row-level security') ||
    msg.includes('permission denied') ||
    msg.includes('insufficient_privilege')
  );
};

function StatusBadge({ status }: { status: string }) {
  return (
    <Badge
      variant="outline"
      className={`text-[10px] px-2 py-0.5 font-semibold rounded-md border ${STATUS_COLORS[status] || 'bg-muted'}`}
    >
      {STATUS_LABELS[status] || status}
    </Badge>
  );
}

export default function InlineProjectStatusCell({ project, canEdit = true }: Props) {
  const [open, setOpen] = useState(false);
  const qc = useQueryClient();
  const mutation = useUpdateProjectStatus();
  const isService = project.status === 'service';

  if (!canEdit) {
    return <StatusBadge status={project.status} />;
  }

  const handlePick = (next: ProjectStatus) => {
    setOpen(false);
    if (next === project.status) return;

    const snapshots = qc.getQueriesData({ queryKey: ['projects-optimized-v2'] }).map(
      ([key, data]) => {
        qc.setQueryData(key, (old: any) => {
          if (!old?.projects) return old;
          return {
            ...old,
            projects: old.projects.map((p: any) =>
              p.id === project.id ? { ...p, status: next } : p,
            ),
          };
        });
        return [key, data] as const;
      },
    );

    mutation.mutate(
      { projectId: project.id, newStatus: next },
      {
        onError: (err: any) => {
          snapshots.forEach(([k, d]) => qc.setQueryData(k, d));
          if (isRlsError(err)) toast.error('Bạn không có quyền đổi trạng thái');
        },
      },
    );
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          onClick={(e) => e.stopPropagation()}
          className="cursor-pointer inline-flex items-center gap-1"
          disabled={mutation.isPending}
        >
          {mutation.isPending ? (
            <span className="inline-flex items-center gap-1 text-[10px] text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" /> Đang lưu…
            </span>
          ) : (
            <>
              <StatusBadge status={project.status} />
              <ChevronDown className="h-3 w-3 text-muted-foreground" />
            </>
          )}
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-48 p-1"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        {isService ? (
          <div className="px-2 py-2 text-[11px] text-muted-foreground">
            Chỉnh status Service qua Edit dialog
          </div>
        ) : (
          OPTIONS.map((opt) => {
            const isCurrent = opt.value === project.status;
            return (
              <Button
                key={opt.value}
                variant="ghost"
                size="sm"
                className={cn('w-full justify-start gap-2 h-8', isCurrent && 'font-semibold')}
                onClick={() => handlePick(opt.value)}
              >
                <Check className={cn('h-3 w-3', isCurrent ? 'opacity-100' : 'opacity-0')} />
                {opt.label}
              </Button>
            );
          })
        )}
      </PopoverContent>
    </Popover>
  );
}
